// PRD §15.6 — live transcript embed.
//
// Route: /embed/conversation/:conversationId/live_transcript
//
// Subscribes to `/threads/{thread_id}/events` SSE and renders a transcript
// stream as `raw_transcript_emitted` / `tagged_transcript_ready` /
// `cleaned_transcript_ready` events arrive.
//
// Rules per PRD §15.6 (Locked Decision):
//   - Speaker labels HIDDEN until a `tagged_transcript_ready` event lands
//     for that batch. Until then we render the bubble as a neutral
//     "speaker pending" marker.
//   - When `tagged_transcript_ready` for a previously-raw batch lands, we
//     recolor the bubble with "You" / "Other".
//   - `cleaned_transcript_ready` replaces the bubble's text with the
//     scrubbed version.
//   - Typing-indicator bubble pinned at end while any batch is pending.
//   - Auto-scroll to bottom on each new event unless user scrolled up.
//
// Token refresh (PRD §7.3): on `ethosphere:token-refreshed`, we tear down
// and re-establish the EventSource so the new bearer token is used.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Center,
  Spinner,
  Text,
  VStack,
  Flex,
  HStack,
  Badge,
} from "@chakra-ui/react";
import EmbedLayout from "./EmbedLayout";
import { getConversation, buildThreadEventsUrl } from "../../services/embed";
import {
  currentEmbedToken,
  TOKEN_REFRESHED_EVENT,
} from "../../EmbedAuthProvider";

// EventSource factory pulled out so tests can substitute a fake.
// We only reference `window` (Jest's jsdom + every browser provides it);
// the create-react-app ESLint config flags `globalThis` as undefined
// even though it's available everywhere modern, so stick with window.
const defaultEventSourceFactory = (url) => new window.EventSource(url);

const SCROLL_PIN_THRESHOLD_PX = 64;

function buildBatchKey(evt) {
  const ord =
    evt?.payload?.batch_ordinal ?? evt?.batch_ordinal ?? evt?.detail_item_id;
  return ord !== undefined && ord !== null ? `b:${ord}` : `id:${evt?.id}`;
}

/**
 * Pure reducer over event envelopes. Exported for unit testing.
 *
 * Each batch is one row in the transcript, keyed by `batch_ordinal` (or
 * detail_item_id fallback). The row carries:
 *   - stage:    "raw" | "tagged" | "cleaned"
 *   - speaker:  null until tagging arrives; "You" / "Other" thereafter
 *   - text:     populated as we receive snippet payloads (best-effort —
 *               the SSE envelope is metadata-only; we render the stage
 *               marker since the snippet text comes from a follow-up
 *               GET in production)
 *   - failed:   true if a batch_processing_failed event landed for it
 */
export function reduceEvent(state, evt) {
  if (!evt || typeof evt !== "object") return state;
  const type = evt.type;
  const key = buildBatchKey(evt);
  const existing = state.batches[key] || {
    key,
    stage: "pending",
    speaker: null,
    text: null,
    failed: false,
    failureReason: null,
    detailItemId: null,
    batchOrdinal: null,
    occurredAt: null,
  };

  const batchOrdinal =
    evt?.payload?.batch_ordinal ?? existing.batchOrdinal ?? null;
  const detailItemId = evt?.detail_item_id ?? existing.detailItemId ?? null;
  const occurredAt = evt?.occurred_at ?? existing.occurredAt ?? null;

  let nextBatch = existing;
  let nextRunId = state.lastRunId;
  let nextRunStatus = state.runStatus;
  let nextLastError = state.lastError;

  switch (type) {
    case "raw_transcript_emitted":
      nextBatch = {
        ...existing,
        stage: existing.stage === "cleaned" || existing.stage === "tagged"
          ? existing.stage
          : "raw",
        // PRD §15.6 — speaker label hidden until tagging.
        speaker: null,
        text: existing.text,
        detailItemId,
        batchOrdinal,
        occurredAt,
      };
      break;
    case "tagged_transcript_ready":
      nextBatch = {
        ...existing,
        stage: existing.stage === "cleaned" ? "cleaned" : "tagged",
        speaker: existing.speaker || "Speaker",
        detailItemId,
        batchOrdinal,
        occurredAt,
      };
      break;
    case "cleaned_transcript_ready":
      nextBatch = {
        ...existing,
        stage: "cleaned",
        // Cleaner runs after tagger; if tagger somehow hasn't landed we
        // still leave speaker neutral per PRD locked decision.
        speaker: existing.speaker,
        detailItemId,
        batchOrdinal,
        occurredAt,
      };
      break;
    case "batch_processing_failed":
      nextBatch = {
        ...existing,
        failed: true,
        failureReason: evt?.payload?.reason || "retry_exhausted",
        occurredAt,
      };
      break;
    case "analysis_starting":
      nextRunStatus = "starting";
      break;
    case "analysis_complete":
      nextRunStatus = "complete";
      nextRunId = evt?.payload?.run_id ?? state.lastRunId;
      break;
    case "thread_analysis_failed":
      nextRunStatus = "failed";
      nextLastError = evt?.payload?.reason || "analysis_failed";
      break;
    default:
      return state;
  }

  // Insertion order: maintain by batch arrival, latest at end.
  let order = state.order;
  if (!state.batches[key]) {
    order = [...state.order, key];
  }

  return {
    batches: { ...state.batches, [key]: nextBatch },
    order,
    lastRunId: nextRunId,
    runStatus: nextRunStatus,
    lastError: nextLastError,
  };
}

const initialState = {
  batches: {},
  order: [],
  lastRunId: null,
  runStatus: null,
  lastError: null,
};

function pendingCount(state) {
  return state.order.reduce((n, k) => {
    const b = state.batches[k];
    return b && !b.failed && b.stage !== "cleaned" ? n + 1 : n;
  }, 0);
}

function BatchBubble({ batch }) {
  const speakerVisible = batch.speaker && batch.stage !== "raw";
  return (
    <Flex
      gap={2}
      align="flex-start"
      data-testid="live-transcript-bubble"
      data-stage={batch.stage}
      data-speaker={speakerVisible ? batch.speaker : "hidden"}
      data-failed={batch.failed ? "true" : "false"}
    >
      <Box flex="1">
        {speakerVisible && (
          <Text fontSize="xs" color="gray.500" mb={1}>
            {batch.speaker}
          </Text>
        )}
        <Box
          bg={batch.failed ? "red.50" : "gray.100"}
          borderRadius="lg"
          px={3}
          py={2}
        >
          <HStack spacing={2}>
            <Badge
              size="sm"
              colorScheme={
                batch.failed
                  ? "red"
                  : batch.stage === "cleaned"
                  ? "green"
                  : batch.stage === "tagged"
                  ? "blue"
                  : "gray"
              }
              data-testid="bubble-stage-badge"
            >
              {batch.failed ? "failed" : batch.stage}
            </Badge>
            <Text fontSize="md" whiteSpace="pre-wrap">
              {batch.text || (batch.failed ? "Content unavailable." : "…")}
            </Text>
          </HStack>
        </Box>
      </Box>
    </Flex>
  );
}

function TypingIndicator() {
  return (
    <Flex data-testid="typing-indicator" align="center" gap={2}>
      <Spinner size="xs" />
      <Text fontSize="sm" color="gray.500">
        Processing…
      </Text>
    </Flex>
  );
}

export default function EmbedLiveTranscriptView({
  // Override hooks for tests — production passes none and uses defaults.
  eventSourceFactory = defaultEventSourceFactory,
}) {
  const { conversationId } = useParams();
  const [threadId, setThreadId] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [state, setState] = useState(initialState);

  // Token version state forces SSE re-establishment when iOS rotates.
  const [tokenVersion, setTokenVersion] = useState(0);
  useEffect(() => {
    const handler = () => setTokenVersion((v) => v + 1);
    if (typeof window !== "undefined") {
      window.addEventListener(TOKEN_REFRESHED_EVENT, handler);
      return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, handler);
    }
    return undefined;
  }, []);

  // One-time conversation fetch to discover thread_id.
  useEffect(() => {
    let cancelled = false;
    getConversation(conversationId)
      .then((data) => {
        if (cancelled) return;
        const tid = data?.thread_id ?? data?.threadId ?? null;
        if (tid == null) {
          setBootError("Conversation is not attached to a thread.");
        } else {
          setThreadId(tid);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setBootError(e.message || "Failed to load conversation.");
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // SSE subscription. Re-subscribes on threadId change OR tokenVersion bump.
  useEffect(() => {
    if (!threadId) return undefined;
    const token = currentEmbedToken() || "";
    const url = buildThreadEventsUrl(threadId, token);

    let es;
    try {
      es = eventSourceFactory(url);
    } catch (e) {
      setBootError(e.message || "SSE unavailable.");
      return undefined;
    }

    const onMessage = (msgEvent) => {
      let parsed;
      try {
        parsed =
          typeof msgEvent.data === "string"
            ? JSON.parse(msgEvent.data)
            : msgEvent.data;
      } catch (_e) {
        // Drop malformed lines — backend should never emit them, but we
        // don't want a stray heartbeat to crash the view.
        return;
      }
      setState((s) => reduceEvent(s, parsed));
    };

    // Backend may either set `event:` or fan everything through onmessage.
    // Register both — handlers are idempotent (reducer is keyed by batch
    // ordinal, so duplicate delivery is a no-op).
    if (typeof es.addEventListener === "function") {
      es.addEventListener("message", onMessage);
      [
        "raw_transcript_emitted",
        "tagged_transcript_ready",
        "cleaned_transcript_ready",
        "analysis_starting",
        "analysis_complete",
        "thread_analysis_failed",
        "batch_processing_failed",
      ].forEach((type) => es.addEventListener(type, onMessage));
    } else {
      es.onmessage = onMessage;
    }

    return () => {
      try {
        es.close();
      } catch (_e) {
        // ignore
      }
    };
  }, [threadId, tokenVersion, eventSourceFactory]);

  // Auto-scroll behavior — pin to bottom unless the user scrolled up.
  const scrollRef = useRef(null);
  const stickyRef = useRef(true);

  const onScroll = (e) => {
    const el = e.currentTarget;
    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);
    stickyRef.current = distanceFromBottom <= SCROLL_PIN_THRESHOLD_PX;
  };

  useEffect(() => {
    if (!stickyRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.order, state.batches]);

  const batchesInOrder = useMemo(
    () => state.order.map((k) => state.batches[k]).filter(Boolean),
    [state.order, state.batches]
  );
  const pending = pendingCount(state);

  return (
    <EmbedLayout>
      <Box
        w="100%"
        px={4}
        py={3}
        data-testid="embed-live-transcript-view"
        ref={scrollRef}
        onScroll={onScroll}
        overflowY="auto"
        maxH="100vh"
      >
        {bootError && (
          <Text color="red.500" data-testid="live-transcript-error">
            {bootError}
          </Text>
        )}

        {!bootError && !threadId && (
          <Center py={8}>
            <Spinner size="md" />
          </Center>
        )}

        {!bootError && threadId && batchesInOrder.length === 0 && (
          <Text color="gray.500" data-testid="live-transcript-waiting">
            Waiting for first batch…
          </Text>
        )}

        {!bootError && threadId && batchesInOrder.length > 0 && (
          <VStack align="stretch" spacing={3}>
            {batchesInOrder.map((b) => (
              <BatchBubble key={b.key} batch={b} />
            ))}
            {pending > 0 && <TypingIndicator />}
          </VStack>
        )}
      </Box>
    </EmbedLayout>
  );
}
